import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Plus, 
  Layout, 
  ExternalLink, 
  Pencil, 
  Trash2, 
  Eye,
  Link2,
  GripVertical
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const themes = [
  { value: "purple", label: "Purple", color: "#7c3aed" },
  { value: "blue", label: "Blue", color: "#3b82f6" },
  { value: "green", label: "Green", color: "#10b981" },
  { value: "pink", label: "Pink", color: "#ec4899" },
  { value: "orange", label: "Orange", color: "#f59e0b" }
];

const MicrositesPage = () => {
  const { user } = useAuth();
  const [microsites, setMicrosites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMicrosite, setSelectedMicrosite] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [formTitle, setFormTitle] = useState("");
  const [formBio, setFormBio] = useState("");
  const [formAvatar, setFormAvatar] = useState("");
  const [formTheme, setFormTheme] = useState("purple");
  const [formLinks, setFormLinks] = useState([{ title: "", url: "" }]);

  useEffect(() => {
    fetchMicrosites();
  }, []);

  const fetchMicrosites = async () => {
    try {
      const response = await axios.get(`${API}/microsites`, { withCredentials: true });
      setMicrosites(response.data);
    } catch (error) {
      toast.error("Gagal memuat microsites");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formTitle) {
      toast.error("Title harus diisi");
      return;
    }

    setIsSubmitting(true);
    const data = {
      title: formTitle,
      bio: formBio || null,
      avatar_url: formAvatar || null,
      theme: formTheme,
      links: formLinks.filter(l => l.title && l.url)
    };

    try {
      if (editMode && selectedMicrosite) {
        const response = await axios.patch(
          `${API}/microsites/${selectedMicrosite.microsite_id}`,
          data,
          { withCredentials: true }
        );
        setMicrosites(microsites.map(m => 
          m.microsite_id === selectedMicrosite.microsite_id ? response.data : m
        ));
        toast.success("Microsite berhasil diupdate!");
      } else {
        const response = await axios.post(`${API}/microsites`, data, { withCredentials: true });
        setMicrosites([response.data, ...microsites]);
        toast.success("Microsite berhasil dibuat!");
      }
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menyimpan microsite");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (micrositeId) => {
    if (!confirm("Yakin ingin menghapus microsite ini?")) return;

    try {
      await axios.delete(`${API}/microsites/${micrositeId}`, { withCredentials: true });
      setMicrosites(microsites.filter(m => m.microsite_id !== micrositeId));
      toast.success("Microsite berhasil dihapus!");
    } catch (error) {
      toast.error("Gagal menghapus microsite");
    }
  };

  const openEditDialog = (microsite) => {
    setSelectedMicrosite(microsite);
    setFormTitle(microsite.title);
    setFormBio(microsite.bio || "");
    setFormAvatar(microsite.avatar_url || "");
    setFormTheme(microsite.theme);
    setFormLinks(microsite.links.length > 0 ? microsite.links : [{ title: "", url: "" }]);
    setEditMode(true);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormTitle("");
    setFormBio("");
    setFormAvatar("");
    setFormTheme("purple");
    setFormLinks([{ title: "", url: "" }]);
    setSelectedMicrosite(null);
  };

  const addLink = () => {
    setFormLinks([...formLinks, { title: "", url: "" }]);
  };

  const updateLink = (index, field, value) => {
    const newLinks = [...formLinks];
    newLinks[index][field] = value;
    setFormLinks(newLinks);
  };

  const removeLink = (index) => {
    setFormLinks(formLinks.filter((_, i) => i !== index));
  };

  const getThemeColor = (themeName) => {
    const theme = themes.find(t => t.value === themeName);
    return theme ? theme.color : "#7c3aed";
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="microsites-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
              Microsites
            </h1>
            <p className="text-muted-foreground mt-1">
              Buat halaman bio link yang menarik
            </p>
          </div>
          <Button onClick={openCreateDialog} data-testid="create-microsite-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Microsite
          </Button>
        </div>

        {/* Microsites Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : microsites.length === 0 ? (
          <Card className="dashboard-card">
            <CardContent className="py-12">
              <div className="text-center">
                <Layout className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-medium mb-2">Belum ada microsite</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Buat microsite pertama Anda untuk berbagi semua link dalam satu halaman
                </p>
                <Button onClick={openCreateDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Microsite
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {microsites.map((microsite) => (
              <Card 
                key={microsite.microsite_id} 
                className="dashboard-card group"
                data-testid={`microsite-${microsite.microsite_id}`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {microsite.avatar_url ? (
                        <img 
                          src={microsite.avatar_url} 
                          alt={microsite.title}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                          style={{ backgroundColor: getThemeColor(microsite.theme) }}
                        >
                          {microsite.title.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-lg">{microsite.title}</CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {microsite.views} views
                        </p>
                      </div>
                    </div>
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getThemeColor(microsite.theme) }}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {microsite.bio && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {microsite.bio}
                    </p>
                  )}
                  <p className="text-xs text-primary font-mono mb-4">
                    {window.location.origin}/m/{microsite.slug}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => window.open(`/m/${microsite.slug}`, "_blank")}
                      data-testid={`view-${microsite.microsite_id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => openEditDialog(microsite)}
                      data-testid={`edit-${microsite.microsite_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-destructive"
                      onClick={() => handleDelete(microsite.microsite_id)}
                      data-testid={`delete-${microsite.microsite_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? "Edit Microsite" : "Create Microsite"}</DialogTitle>
              <DialogDescription>
                {editMode ? "Update detail microsite Anda" : "Buat halaman bio link baru"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left: Form */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      placeholder="My Bio Links"
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                      data-testid="microsite-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Bio</Label>
                    <Textarea
                      placeholder="Deskripsi singkat tentang Anda..."
                      value={formBio}
                      onChange={(e) => setFormBio(e.target.value)}
                      rows={3}
                      data-testid="microsite-bio-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Avatar URL</Label>
                    <Input
                      placeholder="https://example.com/avatar.jpg"
                      value={formAvatar}
                      onChange={(e) => setFormAvatar(e.target.value)}
                      data-testid="microsite-avatar-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Theme</Label>
                    <Select value={formTheme} onValueChange={setFormTheme}>
                      <SelectTrigger data-testid="microsite-theme-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {themes.map((theme) => (
                          <SelectItem key={theme.value} value={theme.value}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: theme.color }}
                              />
                              {theme.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Links</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={addLink}
                        data-testid="add-link-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Link
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {formLinks.map((link, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <Input
                            placeholder="Title"
                            value={link.title}
                            onChange={(e) => updateLink(index, "title", e.target.value)}
                            className="flex-1"
                            data-testid={`link-title-${index}`}
                          />
                          <Input
                            placeholder="URL"
                            value={link.url}
                            onChange={(e) => updateLink(index, "url", e.target.value)}
                            className="flex-1"
                            data-testid={`link-url-${index}`}
                          />
                          {formLinks.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 text-destructive flex-shrink-0"
                              onClick={() => removeLink(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right: Preview */}
                <div>
                  <Label className="mb-4 block">Preview</Label>
                  <div className="phone-frame mx-auto">
                    <div className="phone-frame-content overflow-y-auto">
                      <div 
                        className="min-h-full p-6 text-center"
                        style={{ 
                          background: `linear-gradient(180deg, ${getThemeColor(formTheme)}22 0%, transparent 50%)`
                        }}
                      >
                        {formAvatar ? (
                          <img 
                            src={formAvatar}
                            alt="Avatar"
                            className="w-20 h-20 rounded-full mx-auto mb-4 object-cover border-4 border-white shadow-lg"
                          />
                        ) : (
                          <div 
                            className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-white font-bold text-2xl shadow-lg"
                            style={{ backgroundColor: getThemeColor(formTheme) }}
                          >
                            {formTitle ? formTitle.charAt(0).toUpperCase() : "M"}
                          </div>
                        )}
                        <h3 className="font-bold text-lg mb-1" style={{ fontFamily: "Plus Jakarta Sans" }}>
                          {formTitle || "My Bio Links"}
                        </h3>
                        {formBio && (
                          <p className="text-sm text-muted-foreground mb-4">{formBio}</p>
                        )}
                        <div className="space-y-3 mt-6">
                          {formLinks.filter(l => l.title).map((link, index) => (
                            <div
                              key={index}
                              className="p-3 rounded-xl border border-border bg-card hover:scale-105 transition-transform cursor-pointer"
                              style={{ borderColor: `${getThemeColor(formTheme)}33` }}
                            >
                              <span className="font-medium text-sm">{link.title}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="submit" disabled={isSubmitting} data-testid="microsite-submit-btn">
                  {isSubmitting ? "Saving..." : editMode ? "Save Changes" : "Create Microsite"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MicrositesPage;
